import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'src/registry/ui/Button';
import { ICON_MAP } from 'src/utils/constants';

type BackLinkProps = {
    /** Where to go when there is no in-app history to return to. Also the link's href. */
    to: string;
    children: React.ReactNode;
};

/**
 * A back control that returns to the previous screen, falling back to a fixed destination.
 *
 * For screens reachable from many places — a customer opened from the list, from a subscription,
 * from an invoice — history is what the reader means by "back", and it also returns them to the
 * exact list page and filters they left.
 *
 * It stays an anchor rather than a button so the destination is visible on hover, middle-click
 * still opens the fallback in a new tab, and the control is keyboard-reachable as a link. The
 * click handler takes over only when there is somewhere to go back to: on a page opened directly
 * from a bookmark or a new tab, `history.state.idx` is 0 and going back would leave the app.
 */
const BackLink = ({ to, children }: BackLinkProps) => {
    const navigate = useNavigate();

    const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

        const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
        if (historyIndex > 0) {
            event.preventDefault();
            navigate(-1);
        }
    };

    return (
        <Button asChild className="place-self-start p-0" variant="link">
            <Link to={to} onClick={onClick}>
                <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                {children}
            </Link>
        </Button>
    );
};

export default BackLink;
